/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/MDBox";
import SoftTypography from "components/MDTypography";
import AsnStatusController from "Controller/AsnStatusController";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
  Box,
  Divider,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

function ASNStatus() {
  const {
    asnStatus,
    generateRows,
    columns,
    materialcolumns,
    generateMaterialRows,
    cloading,
    confValue,
    handleVendorChange,
    selectedParty,
    handleSelectionChange,
    paginationModel,
    setPaginationModel,
    rowCount,
    openPopup,
    handleClosePopup,
    handleAccept,
    handleReject,
    selectedData,
    editData,
    openEditPopup,
    handleEditClick,
    handleCloseEditPopup,
    editColumns,
    uploadBillDetails,
    searchPONumber,
    setSearchPONumber,
  } = AsnStatusController();
  const rowsLength = generateRows(asnStatus).length;
  const rows = generateRows(asnStatus);
  const materialrows = generateMaterialRows(asnStatus);

  const theme = useTheme();

  const customStyles = {
    option: (provided) => ({
      ...provided,
      zIndex: 999, // Adjust the value as needed
      fontSize: "0.9rem",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000, // Make sure this value is higher than the table header's z-index
      fontSize: "0.9rem",
    }),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* {cloading && <NewLoader></NewLoader>} */}
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox
              pt={2}
              px={2}
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Page Title */}
              <SoftTypography variant="h6" fontWeight="medium">
                ASN Status
              </SoftTypography>

              {/* PO Filter */}
              <TextField
                label="PO Number"
                variant="outlined"
                size="small"
                placeholder="Search PO Number"
                value={searchPONumber}
                onChange={(e) => setSearchPONumber(e.target.value)}
                sx={{ width: 250 }}
              />
            </SoftBox>

            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
              p={1}
            >
              <MDBox pt={3} pb={2} px={2} sx={{ height: "50%", width: "100%" }}>
                {rows.length > 0 ? (
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    disableSelectionOnClick
                    pagination
                    paginationMode="server"
                    // sortingMode="server"
                    // filterMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel} // Use paginationModel directly
                    onPaginationModelChange={setPaginationModel} // Use this instead of separate handlers
                    pageSizeOptions={[100]} // Add page size options
                    autoHeight
                    sx={{
                      flex: 1,
                      "& .MuiDataGrid-columnHeaders": {
                        fontWeight: "bold",
                        textAlign: "center",
                      },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #f0f0f0",
                        textAlign: "center",
                      },
                    }}
                    sortingOrder={["asc", "desc"]}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 100,
                        },
                      },
                      sorting: {
                        sortModel: [{ field: "poDate", sort: "desc" }],
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" height={300}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "1rem" }}>
                      No data available!
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        fullWidth
        maxWidth={false} // disable maxWidth limit
        PaperProps={{
          sx: {
            width: "80vw", // or '1200px'
            maxWidth: "none", // prevent theme override
          },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          Material Details
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "normal", fontSize: "1rem", color: "black" }}
          ></Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            "& th": {
              whiteSpace: "nowrap", // prevent line breaks
              fontSize: "0.875rem", // optional: reduce font size
            },
          }}
        >
          <DataTable
            table={{ columns: materialcolumns, rows: materialrows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>
          <Button
            onClick={handleClosePopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialogue */}
      <Dialog
        open={openEditPopup}
        onClose={handleCloseEditPopup}
        fullWidth
        maxWidth={false} // disable maxWidth limit
        PaperProps={{
          sx: {
            width: "80vw", // or '1200px'
            maxWidth: "none", // prevent theme override
          },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          Add Bill Details
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "normal", fontSize: "1rem", color: "black" }}
          ></Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            "& th": {
              whiteSpace: "nowrap", // prevent line breaks
              fontSize: "0.875rem", // optional: reduce font size
            },
          }}
        >
          <DataTable
            table={{ columns: editColumns, rows: editData }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>
          <MDButton variant="gradient" color="error" onClick={uploadBillDetails}>
            Submit
          </MDButton>
          <Button
            onClick={handleCloseEditPopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ASNStatus;
